import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

/**
 * The dialog from the design, in three kinds: `confirm`, `input`, `danger`.
 *
 * Copy is English, not the prototype's Italian: the old admin is English, every
 * page in this tree is English, and a dialog that switches language mid-flow is
 * worse than one that does not match the mockup's words. Flagged in
 * REWRITE-STATE.md — if Italian is wanted it is a decision for the whole tree,
 * not for this file.
 *
 * It exists to retire `window.confirm`, `window.prompt` and `window.alert` from
 * this admin. Those were not merely ugly: a native prompt cannot say *what* an
 * action will do, so the riskiest operations here — forcing a cancellation,
 * moving a booking onto an occupied slot — were guarded by a single line of text
 * and an OK button. This dialog has room for the consequence, a warning strip
 * and, for `danger`, a standing "irreversible" note next to the confirm button.
 *
 * Deliberately **not** portalled. Everything in this tree renders inside
 * `.next-root`, where its tokens apply; a portal to `document.body` would land
 * outside, unstyled by us and reachable by the old admin's stylesheet. That was
 * the reason `AriaModal` had to go, so its replacement must not repeat it.
 *
 * Which means the focus trap is ours to provide, and it is: Escape closes,
 * focus moves in on open and back on close, Tab cycles within the dialog, and
 * the overlay click closes. `aria-modal` plus a labelled title covers the rest.
 */

const KIND_GLYPH = { confirm: '?', input: '✎', danger: '!' };

export default function Modal({
  open,
  kind = 'confirm',
  title,
  context,
  body,
  warning,
  label,
  placeholder,
  hint,
  multiline = false,
  mono = false,
  // An input on a dialog of any kind — a date on a `danger` cancellation, say.
  // `input` dialogs get a text field without it.
  inputType,
  inputMin,
  // A second, optional free-text field under the input (a reason, a note). Its
  // value is passed to `onConfirm` as the second argument; it never blocks the
  // confirm button.
  noteLabel,
  notePlaceholder,
  previewHtml,
  // A dialog with nothing to confirm — a preview, a rendered email. It gets one
  // button instead of a Cancel and a confirm that both merely close, which is
  // two controls asking a reader to work out that they are the same.
  dismissOnly = false,
  // A value to hand over — a generated password — shown read-only with a copy
  // button. The copy runs on that button's own click, so the browser always
  // counts it as a user gesture and allows it.
  copyValue,
  // Off for a dialog whose content is lost on close, so a stray click beside
  // it cannot throw away what it shows.
  closeOnScrimClick = true,
  initialValue = '',
  confirmLabel = 'Confirm',
  busy = false,
  onConfirm,
  onClose
}) {
  const [value, setValue] = useState(initialValue);
  const [note, setNote] = useState('');
  const [copied, setCopied] = useState(false);
  const cardRef = useRef(null);
  const firstRef = useRef(null);
  const returnTo = useRef(null);

  const isInput = kind === 'input' || !!inputType;

  useEffect(() => {
    if (!open) return undefined;

    setValue(initialValue);
    setNote('');
    setCopied(false);
    returnTo.current = document.activeElement;

    // The input is the point of an input dialog; for the others the safe button
    // is, so a stray Enter or Space cannot fire the destructive one.
    const timer = setTimeout(() => firstRef.current?.focus(), 0);

    const onKey = event => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
        return;
      }

      if (event.key !== 'Tab' || !cardRef.current) return;

      const focusable = cardRef.current.querySelectorAll(
        'button:not([disabled]), input, textarea, [href], [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKey, true);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('keydown', onKey, true);
      // Back to whatever opened it, so a keyboard user is not dropped at the top
      // of the page.
      if (returnTo.current && typeof returnTo.current.focus === 'function') {
        returnTo.current.focus();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialValue]);

  if (!open) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(copyValue);
      setCopied(true);
    } catch (error) {
      // Clipboard is blocked in some contexts; the value is on screen and
      // selectable either way.
    }
  };

  const confirm = () => {
    if (busy) return;
    onConfirm(isInput ? value : undefined, noteLabel ? note.trim() : undefined);
  };

  return (
    <div className="next-modal__scrim" onClick={closeOnScrimClick ? onClose : undefined} role="presentation">
      <div
        className="next-modal"
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={event => event.stopPropagation()}
      >
        <div className="next-modal__head">
          <span className={`next-modal__glyph next-modal__glyph--${kind}`} aria-hidden>
            {KIND_GLYPH[kind] || '?'}
          </span>
          <div className="next-modal__heading">
            <h2>{title}</h2>
            {context && <span className="next-modal__context">{context}</span>}
          </div>
        </div>

        <div className="next-modal__body">
          {body && <p className="next-modal__text">{body}</p>}

          {isInput && (
            <label className="next-modal__field">
              {label && <span className="next-modal__label">{label}</span>}
              {multiline ? (
                <textarea
                  ref={firstRef}
                  rows={3}
                  className="next-modal__input"
                  placeholder={placeholder}
                  value={value}
                  onChange={event => setValue(event.target.value)}
                />
              ) : (
                <input
                  ref={firstRef}
                  type={inputType || 'text'}
                  min={inputMin}
                  className={`next-modal__input${mono ? ' next-modal__input--mono' : ''}`}
                  placeholder={placeholder}
                  value={value}
                  onChange={event => setValue(event.target.value)}
                  onKeyDown={event => {
                    if (event.key === 'Enter') confirm();
                  }}
                />
              )}
              {hint && <span className="next-modal__hint">{hint}</span>}
            </label>
          )}

          {noteLabel && (
            <label className="next-modal__field">
              <span className="next-modal__label">{noteLabel}</span>
              <input
                type="text"
                className="next-modal__input"
                placeholder={notePlaceholder}
                value={note}
                onChange={event => setNote(event.target.value)}
                onKeyDown={event => {
                  if (event.key === 'Enter') confirm();
                }}
              />
            </label>
          )}

          {copyValue !== undefined && (
            <div className="next-modal__copy">
              <input
                readOnly
                className="next-modal__input next-modal__input--mono"
                value={copyValue}
                onFocus={event => event.target.select()}
              />
              <button type="button" className="next-btn next-btn--primary" onClick={copy}>
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
          )}

          {/* A preview of something that will be sent — rendered in a sandboxed
              frame, because it is HTML somebody else wrote. */}
          {previewHtml !== undefined && (
            previewHtml === null
              ? <p className="next-modal__text next-muted">Loading the preview…</p>
              : (
                <iframe
                  className="next-modal__preview"
                  title="Preview"
                  sandbox=""
                  srcDoc={previewHtml}
                />
              )
          )}

          {warning && <div className="next-modal__warning">{warning}</div>}
        </div>

        <div className="next-modal__foot">
          {kind === 'danger' && !dismissOnly && (
            <span className="next-modal__irreversible">Irreversible action</span>
          )}
          {!dismissOnly && (
            <button
              type="button"
              className="next-btn"
              ref={isInput ? null : firstRef}
              onClick={onClose}
            >
              Cancel
            </button>
          )}
          <button
            type="button"
            className={`next-btn ${dismissOnly ? '' : (kind === 'danger' ? 'next-btn--danger-solid' : 'next-btn--primary')}`}
            ref={dismissOnly ? firstRef : null}
            disabled={busy || (isInput && !value.trim())}
            onClick={dismissOnly ? onClose : confirm}
          >
            {busy ? '…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

Modal.propTypes = {
  open: PropTypes.bool,
  kind: PropTypes.oneOf(['confirm', 'input', 'danger']),
  title: PropTypes.string,
  context: PropTypes.string,
  body: PropTypes.string,
  warning: PropTypes.string,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  hint: PropTypes.string,
  multiline: PropTypes.bool,
  mono: PropTypes.bool,
  inputType: PropTypes.string,
  inputMin: PropTypes.string,
  noteLabel: PropTypes.string,
  notePlaceholder: PropTypes.string,
  previewHtml: PropTypes.string,
  dismissOnly: PropTypes.bool,
  copyValue: PropTypes.string,
  closeOnScrimClick: PropTypes.bool,
  initialValue: PropTypes.string,
  confirmLabel: PropTypes.string,
  busy: PropTypes.bool,
  onConfirm: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired
};
